import { NextResponse } from 'next/server'
import { match, MatchResult, ParamData } from 'path-to-regexp'

import { RequiredSessionAction } from '../graphql'

import { validateRedirectUrl } from './validate-redirect-url'

export const PUBLIC_PATHS = ['/public', '/forgot-password', '/logout', '/set-password']
export const AUTH_SCOPES = ['/login', '/2fa']

export interface SessionWithMFA {
  mfa:
    | {
        verified: boolean
      }
    | undefined

  requiredAction: RequiredSessionAction | undefined
  isLoggedIn: boolean
  currentOrganization: { resources: Array<{ identifier: string }> } | undefined
}

/**
 * List of guards that can be used to redirect the user to a different page.
 *
 * Return true to redirect to the original page, or a URL to redirect to.
 * Return false to allow the user to access the page.
 */
const GUARD_DEFINITIONS: Record<
  string,
  (session: SessionWithMFA, match: MatchResult<ParamData>, originalURL: URL) => boolean | URL
> = {
  ['/login']: (session) => !session,
  ['/2fa/verify']: (session) => session?.requiredAction === RequiredSessionAction.MFA_VERIFY,
  ['/2fa/account-recovery']: (session) => session?.requiredAction === RequiredSessionAction.MFA_VERIFY,
  ['/2fa/recovery']: (session) => session?.requiredAction === RequiredSessionAction.MFA_VERIFY,
  ['/2fa/setup']: (session) =>
    session?.requiredAction === RequiredSessionAction.MFA_SETUP || (!!session?.isLoggedIn && !session.mfa),
  ['/admin/:venueShortId']: (session, match, originalURL) => {
    if (!session.isLoggedIn) {
      return false
    }
    // the user needs to select a venue => allow access
    if (!session.currentOrganization) {
      return true
    }

    // the user is in the scope of the current organization
    if (
      session.currentOrganization.resources.some(
        ({ identifier }) => identifier === (match as MatchResult<{ venueShortId: string }>).params.venueShortId,
      )
    ) {
      return true
    }

    // the user needs to re-enter a password to access the venue
    return appendForwardUrl(
      `/venue/${(match as MatchResult<{ venueShortId: string }>).params.venueShortId}/login`,
      originalURL,
    )
  },
  ['/admin/:venueShortId/*any']: (session, match, originalURL) => {
    if (!session.isLoggedIn) {
      return false
    }
    const { venueShortId } = (match as MatchResult<{ venueShortId: string }>).params
    // force the user to select a venue
    if (!session.currentOrganization) {
      return appendForwardUrl(`/admin/${venueShortId}`, originalURL)
    }

    // the user is in the scope of the current organization
    if (session.currentOrganization.resources.some(({ identifier }) => identifier === venueShortId)) {
      return true
    }

    // the user needs to re-enter a password to access the venue
    return appendForwardUrl(`/venue/${venueShortId}/login`, originalURL)
  },
}

const GUARD_MATCHER = Object.entries(GUARD_DEFINITIONS).map(([pattern, guard]) => [match(pattern), guard] as const)
function processGuard(session: SessionWithMFA, requestUrl: URL): boolean | URL {
  for (const [matcher, canAccess] of GUARD_MATCHER) {
    const match = matcher(requestUrl.pathname)
    if (match) {
      // a matching guard was found. Check if the user is allowed to access the page.
      return canAccess(session, match, requestUrl)
    }
  }

  // no guard matched. Allow access to the page.
  return true
}

const appendForwardUrl = (redirectPathname: string, originalUrl: URL, validPaths: Array<string> = []): URL => {
  // We are already in the correct scope, so no need to redirect
  if ([redirectPathname, ...validPaths].some((pattern) => match(pattern)(originalUrl.pathname) !== false)) {
    return originalUrl
  }

  // Attach the forward url to the redirect path
  const to = new URL(redirectPathname, originalUrl.origin)

  // Either forward the url parameter from the previous page or attach the (non-auth) page we requested originally.
  const url = validateRedirectUrl(
    originalUrl.searchParams.get('url') || [originalUrl.pathname, originalUrl.search].join(''),
  )

  // Only set it if it's not the root page. / is the default anyway.
  if (url !== '/') {
    to.searchParams.set('url', url)
  }

  return to
}

export const createRedirectResponse = (redirect: URL, isServerAction: boolean): NextResponse => {
  if (isServerAction) {
    return new NextResponse(null, {
      status: 303,
      headers: { 'x-action-redirect': redirect.toString() },
    })
  }

  // 303, not the 307 that `NextResponse.redirect` defaults to. A guard redirect must never
  // preserve the request method: on a 307 the browser replays the POST body - including the
  // `$ACTION_ID_*` field of a Server Action - against the redirect target. That page does not own
  // the action, so Next answers `Failed to find Server Action` with a 500 (see #65535). 303 makes
  // the browser re-issue the redirect as a GET, which is what a guard wants in the first place.
  return NextResponse.redirect(redirect, 303)
}

export const processGuards = (session: SessionWithMFA | undefined, requestUrl: URL): URL => {
  // No session exists. Force the user to the login page.
  if (!session) {
    return appendForwardUrl('/login', requestUrl)
  }

  // The user has to complete the setup process. This is the case after using a recovery code
  if (session.requiredAction === RequiredSessionAction.MFA_SETUP) {
    return appendForwardUrl('/2fa/setup', requestUrl)
  }

  // We can only access 2fa verify and recovery pages after a successful login.
  if (session.requiredAction === RequiredSessionAction.MFA_VERIFY) {
    return appendForwardUrl('/2fa/verify', requestUrl, ['/2fa/account-recovery', '/2fa/recovery'])
  }

  const canAccess = processGuard(session, requestUrl)

  // a custom URL was defined in the guard. Redirect to that.
  if (canAccess instanceof URL) {
    return canAccess
  }
  if (canAccess) {
    return requestUrl
  }
  // The user is not allowed to access the page.
  return new URL(validateRedirectUrl(requestUrl.searchParams.get('url')), requestUrl.origin)
}
